import { Json } from "@redotech/json/json";
import {
  OptionalStringParam,
  useParam,
} from "@redotech/react-router-util/param";
import { usePrompt } from "@redotech/react-router-util/prompt";
import { useHandler } from "@redotech/react-util/hook";
import {
  FlowType,
  ReturnFlow as ModelReturnFlow,
  returnFlowJsonFormat,
} from "@redotech/redo-model/return-flow";
import { Team } from "@redotech/redo-model/team";
import {
  modelToSteps,
  stepsToModel,
} from "@redotech/redo-return-ui/flows/model-to-steps";
import { Step, StepId, StepType } from "@redotech/redo-return-ui/flows/step";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { sinkPromise } from "@redotech/util/promise";
import { browserIsMac } from "@redotech/web-util/browser";
import {
  Connection,
  Controls,
  Edge,
  Node,
  ReactFlowInstance,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  memo,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ConnectionModeOverlay } from "./components/connection-mode-overlay";
import { FlowCanvas } from "./components/flow-canvas";
// import { FlowAgentChat } from "./components/flow-agent/flow-agent-chat";
// import { FlowAgentQAChat } from "./components/flow-agent/flow-agent-qa-chat";
import { FlowAgentChat } from "./components/flow-agent/flow-agent-chat";
import { FlowEditorToolbar } from "./components/flow-editor-toolbar";
import { FlowEditorTopHeader } from "./components/flow-editor-top-header";
import { FlowLeftPanel, WarningStep } from "./components/flow-left-panel";
import { NodeData } from "./components/flow-step-node";
import { RightPanel } from "./components/right-panel";
import { modelToStepsDetailsProps } from "./flow-editor";
import { FlowEditorHeaderButtons } from "./flow-editor-header-buttons";
import { useConnectionMode } from "./hooks/use-connection-mode";
import { useDragDrop } from "./hooks/use-drag-drop";
import { useFlowNodesAndEdges } from "./hooks/use-flow-nodes-and-edges";
import { useKeyboardShortcuts } from "./hooks/use-keyboard-shortcuts";
import * as styles from "./new-flow-editor.module.css";
import { useFlowCopyPaste } from "./use-flow-copy-paste";
import { useUndoRedo } from "./use-undo-redo";
import {
  edgesAreEqual,
  undoRedoStateEquals,
} from "./utils/flow-editor-comparisons";
import { UndoRedoState } from "./utils/flow-editor-types";
import {
  getStepIdsInModelOrder,
  reconstructNodePositionsMap,
} from "./utils/order-steps";
import {
  removeStepConnection,
  updateStepConnection,
} from "./utils/update-step-connection";

const GRID_SIZE = 24;

interface FlowEditorEditModeProps {
  mode?: "edit";
  questionFlow: Json;
  handleSave: (
    newFlow: Json,
    signal?: AbortSignal,
    ruleName?: string,
    ruleDescription?: string,
  ) => Promise<void>;
  saveLoad: { pending: boolean };
  publishLoad?: { pending: boolean };
  handlePublish?: () => Promise<void>;
  handleUnpublish?: () => Promise<void>;
  defaultQuestionFlow: ModelReturnFlow;
  firstStepTitle?: string;
  flowTitle: string;
  stepOptions: StepType<any, any>[];
  returnReasonTitle?: string;
  flowType: FlowType;
  ruleDetails?: { name: string | undefined; description: string | undefined };
  defaultButtonClicked?: () => void;
  defaultButtonPending?: boolean;
  defaultButtonText?: string;
  onFlowStepChange?: (
    changedStep: { id: StepId; step: Readonly<Step> },
    existingSteps: ReadonlyMap<StepId, Readonly<Step>>,
    setStep: (id: StepId, step: SetStateAction<Step | undefined>) => void,
  ) => void;
  closeNavigationPath?: string;
  actionsSectionName?: string;
  warningSteps?: WarningStep[];
  startIcon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  team?: Team;
  onReverted?(flow: unknown): void;
}

interface FlowEditorReadonlyModeProps {
  mode: "readonly";
  steps: Map<string, Step>;
  flowType: FlowType;
  nodePositionsArray: { x: number; y: number }[] | undefined;
  team: Team | undefined;
  highlightedNodeIds?: Set<string>;
  currentStepId?: string;
  highlightedEdgePairs?: Set<string>;
  onNodeClick?: (event: React.MouseEvent, node: Node<NodeData>) => void;
}

type NewFlowEditorProps = FlowEditorEditModeProps | FlowEditorReadonlyModeProps;

const NewFlowEditorInner = memo(function NewFlowEditorInner({
  questionFlow,
  defaultQuestionFlow,
  handleSave,
  saveLoad,
  publishLoad,
  firstStepTitle,
  flowTitle,
  stepOptions,
  returnReasonTitle,
  flowType,
  handlePublish,
  handleUnpublish,
  ruleDetails,
  defaultButtonClicked,
  defaultButtonPending,
  defaultButtonText,
  closeNavigationPath,
  actionsSectionName,
  warningSteps,
  startIcon,
  team,
  onReverted,
}: FlowEditorEditModeProps) {
  const reactFlowInstance = useReactFlow();
  const isMac = browserIsMac();

  const parsedFlow = useMemo(() => {
    try {
      let model = returnFlowJsonFormat.read(questionFlow);
      const hasExistingFlow = model.steps.length > 0;

      // If there isn't a flow saved, load the default flow
      if (!hasExistingFlow) {
        model = defaultQuestionFlow;
      }

      return { model, hasExistingFlow };
    } catch (error) {
      return { model: defaultQuestionFlow, hasExistingFlow: false };
    }
  }, [questionFlow, defaultQuestionFlow]);

  const initialNodePositions: Map<string, { x: number; y: number }> =
    useMemo(() => {
      try {
        const { model } = parsedFlow;
        const stepsFromModel = modelToSteps({
          model,
          firstStepTitle,
          returnReasonTitle,
          flowType,
          startIcon,
          ...modelToStepsDetailsProps,
        });
        return reconstructNodePositionsMap(model.nodePositions, stepsFromModel);
      } catch (error) {
        return new Map();
      }
    }, [parsedFlow, firstStepTitle, returnReasonTitle, flowType, startIcon]);

  const initialUndoRedoState = useMemo<UndoRedoState>(() => {
    try {
      const { model } = parsedFlow;

      const steps = modelToSteps({
        model,
        firstStepTitle,
        returnReasonTitle,
        flowType,
        startIcon,
        ...modelToStepsDetailsProps,
      });
      return {
        steps,
        userNodePositions: new Map(initialNodePositions),
        selectedId: undefined,
      };
    } catch (error) {
      return {
        steps: new Map(),
        userNodePositions: new Map(),
        selectedId: undefined,
      };
    }
  }, [
    parsedFlow,
    firstStepTitle,
    returnReasonTitle,
    flowType,
    initialNodePositions,
    startIcon,
  ]);

  const {
    state: currentState,
    setState: setUndoRedoState,
    undo,
    redo,
    canUndo,
    canRedo,
    reset: resetUndoRedo,
  } = useUndoRedo(initialUndoRedoState, undoRedoStateEquals);

  const steps = currentState.steps;
  const userNodePositions = currentState.userNodePositions;
  const selectedId = currentState.selectedId;

  const [modified, setModified] = useState(false);

  usePrompt(
    modified
      ? "You have unsaved changes. Would you like to discard these changes?"
      : null,
    false,
  );

  const { handleCopy, handlePaste, selectedNodeIdsRef, clearSelectedNodeIds } =
    useFlowCopyPaste(
      reactFlowInstance,
      steps,
      userNodePositions,
      GRID_SIZE,
      (updates) => {
        setUndoRedoState((prev) => ({
          ...prev,
          steps: updates.steps,
          userNodePositions: updates.userNodePositions,
        }));
      },
      () => setModified(true),
    );

  const [published, setPublished] = useState<boolean | undefined>(undefined);
  const initialRuleName = ruleDetails?.name || "";
  const initialRuleDescription = ruleDetails?.description || "";
  const [ruleName, setRuleName] = useState<string>(initialRuleName);
  const [ruleDescription, setRuleDescription] = useState<string>(
    initialRuleDescription,
  );

  const [isSelectMode, setIsSelectMode] = useState(true);

  useEffect(() => {
    const { model, hasExistingFlow } = parsedFlow;

    const newSteps = modelToSteps({
      model,
      firstStepTitle,
      returnReasonTitle,
      flowType,
      startIcon,
      ...modelToStepsDetailsProps,
    });
    const nodePositions = reconstructNodePositionsMap(
      model.nodePositions,
      newSteps,
    );

    resetUndoRedo({
      steps: newSteps,
      userNodePositions: nodePositions,
      selectedId: undefined,
    });

    if (hasExistingFlow) {
      setPublished(!!model.publishedAt);
      setModified(false);
    } else {
      setPublished(false);
      setModified(true);
    }
  }, [
    parsedFlow,
    firstStepTitle,
    returnReasonTitle,
    flowType,
    resetUndoRedo,
    startIcon,
  ]);

  useEffect(() => {
    if (
      initialRuleDescription !== ruleDescription ||
      initialRuleName !== ruleName
    ) {
      setModified(true);
    }
  }, [ruleName, ruleDescription, initialRuleName, initialRuleDescription]);

  const handleDeleteStep = useHandler((stepIdToDelete: StepId) => {
    if (stepIdToDelete === "start") return;

    setUndoRedoState((prev) => {
      const updatedSteps = new Map(
        [...prev.steps.entries()]
          .filter(([id]) => id !== stepIdToDelete)
          .map(([id, s]) => [id, s.stepDeleted(stepIdToDelete)]),
      );

      return {
        ...prev,
        steps: updatedSteps,
        selectedId:
          prev.selectedId === stepIdToDelete ? undefined : prev.selectedId,
      };
    });
    setModified(true);

    if (selectedId === stepIdToDelete) {
      setSelectedParam(undefined);
    }

    setNodes((nds) => nds.filter((n) => n.id !== stepIdToDelete));
    setEdges((eds) =>
      eds.filter(
        (e) => e.source !== stepIdToDelete && e.target !== stepIdToDelete,
      ),
    );
  });

  const handleDelete = useHandler(() => {
    if (!reactFlowInstance) return;

    const selectedNodes = reactFlowInstance
      .getNodes()
      .filter((n) => n.selected);
    const selectedEdges = reactFlowInstance
      .getEdges()
      .filter((e) => e.selected);

    if (selectedNodes.length === 0 && selectedEdges.length === 0) return;

    const deletingSelectedNode = selectedNodes.some((n) => n.id === selectedId);

    setUndoRedoState((prev) => {
      let updatedSteps = new Map(prev.steps);

      for (const edge of selectedEdges) {
        if (!edge.source || !edge.target) continue;
        const sourceStep = updatedSteps.get(edge.source);
        if (!sourceStep) continue;

        const updatedStep = removeStepConnection(sourceStep, edge.target);
        updatedSteps.set(edge.source, updatedStep);
      }

      for (const node of selectedNodes) {
        if (node.id === "start") continue;
        updatedSteps = new Map(
          [...updatedSteps.entries()]
            .filter(([id]) => id !== node.id)
            .map(([id, s]) => [id, s.stepDeleted(node.id)]),
        );
      }

      return {
        ...prev,
        steps: updatedSteps,
        selectedId: deletingSelectedNode ? undefined : prev.selectedId,
      };
    });
    setModified(true);

    if (deletingSelectedNode) {
      setSelectedParam(undefined);
    }

    const selectedNodeIds = new Set(selectedNodes.map((n) => n.id));
    const selectedEdgeIds = new Set(selectedEdges.map((e) => e.id));

    setNodes((nds) => nds.filter((n) => !selectedNodeIds.has(n.id)));
    setEdges((eds) => eds.filter((e) => !selectedEdgeIds.has(e.id)));
  });

  const [selectedParam, setSelectedParam] = useParam(
    new OptionalStringParam("selected"),
  );

  useEffect(() => {
    if (selectedParam && selectedParam !== selectedId) {
      setUndoRedoState((prev) => ({ ...prev, selectedId: selectedParam }), {
        skipHistory: true,
      });
    }
  }, [selectedParam, selectedId, setUndoRedoState]);

  const selectedStep = useMemo(() => {
    if (!selectedId || !steps.has(selectedId)) return null;
    return steps.get(selectedId) || null;
  }, [selectedId, steps]);

  const setStepWithId = useHandler(
    (
      stepId: StepId,
      step: SetStateAction<Step | undefined>,
      options?: { skipHistory?: boolean },
    ) => {
      setUndoRedoState((prev) => {
        let updatedSteps = prev.steps;

        if (step instanceof Function) {
          step = step(updatedSteps.get(stepId)!);
        }
        if (step) {
          updatedSteps = new Map(updatedSteps);
          updatedSteps.set(stepId, step);
        } else {
          updatedSteps = new Map(
            [...updatedSteps.entries()]
              .filter(([id]) => id !== stepId)
              .map(([id, s]) => [id, s.stepDeleted(stepId)]),
          );
        }

        return { ...prev, steps: updatedSteps };
      }, options);
      setModified(true);
    },
  );

  const setSelectedId = useHandler((id: string | undefined) => {
    setUndoRedoState((prev) => ({ ...prev, selectedId: id }), {
      skipHistory: true,
    });
    setSelectedParam(id);
  });

  const {
    connectionMode,
    mousePosition,
    hoveredNodeId,
    startConnectionMode,
    cancelConnectionMode,
    onNodeClick,
    onNodeMouseEnter,
    onNodeMouseLeave,
    onPaneClick,
  } = useConnectionMode({
    reactFlowInstance,
    steps,
    setStepWithId,
    setModified,
    setSelectedId,
    clearSelectedNodeIds,
  });

  const { onDragOver, onDrop } = useDragDrop({
    reactFlowInstance,
    steps,
    stepOptions,
    gridSize: GRID_SIZE,
    setUndoRedoState,
    setModified,
    setSelectedParam,
  });

  useKeyboardShortcuts({
    handleCopy,
    handlePaste,
    undo,
    redo,
    handleDelete,
    connectionMode,
    cancelConnectionMode,
  });

  const {
    nodes: initialNodes,
    edges: initialEdges,
    computedPositions,
  } = useFlowNodesAndEdges({
    steps,
    flowType,
    nodePositions: userNodePositions,
    selectedNodeIdsRef,
    team,
    warningSteps,
  });

  // If start node position was calculated (not in userNodePositions), save it
  useEffect(() => {
    if (!userNodePositions.has("start") && computedPositions.has("start")) {
      const startPosition = computedPositions.get("start")!;
      setUndoRedoState(
        (prev) => ({
          ...prev,
          userNodePositions: new Map(prev.userNodePositions).set(
            "start",
            startPosition,
          ),
        }),
        { skipHistory: true },
      );
    }
  }, [computedPositions, userNodePositions, setUndoRedoState]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // initialize data
  useEffect(() => {
    const validNodeIds = new Set(initialNodes.map((n) => n.id));

    const validEdges = initialEdges.filter(
      (edge) => validNodeIds.has(edge.source) && validNodeIds.has(edge.target),
    );

    setNodes(initialNodes);

    setEdges((currentEdges) => {
      if (edgesAreEqual(currentEdges, validEdges)) {
        return currentEdges;
      }
      return validEdges;
    });

    if (selectedNodeIdsRef.current.size > 0) {
      setNodes((nds) =>
        nds.map((node) => ({
          ...node,
          selected: selectedNodeIdsRef.current.has(node.id),
        })),
      );
    }
  }, [initialNodes, initialEdges, setNodes, setEdges, selectedNodeIdsRef]);

  const handleNodesChange = useHandler(
    (changes: Parameters<typeof onNodesChange>[0]) => {
      onNodesChange(changes);

      const hasDragEnd = changes.some(
        (change) => change.type === "position" && change.dragging === false,
      );
      if (hasDragEnd && selectedNodeIdsRef.current.size > 0) {
        clearSelectedNodeIds();
      }

      const positionUpdates = new Map<string, { x: number; y: number }>();
      const removals: string[] = [];

      for (const change of changes) {
        switch (change.type) {
          case "position":
            if (
              change.dragging === false &&
              change.position &&
              !isNaN(change.position.x) &&
              !isNaN(change.position.y)
            ) {
              positionUpdates.set(change.id, change.position);
            }
            break;
          case "remove":
            removals.push(change.id);
            setStepWithId(change.id, undefined);
            setModified(true);
            break;
        }
      }

      if (positionUpdates.size > 0) {
        setUndoRedoState((prev) => {
          const newPositions = new Map(prev.userNodePositions);
          for (const [id, position] of positionUpdates.entries()) {
            newPositions.set(id, position);
          }
          return { ...prev, userNodePositions: newPositions };
        });
        setModified(true);
      }
    },
  );

  const onConnect = useHandler((connection: Connection) => {
    if (!connection.source || !connection.target) return;

    const sourceStep = steps.get(connection.source);
    const targetStep = steps.get(connection.target);

    if (!sourceStep || !targetStep) {
      return;
    }

    const handleId = connection.sourceHandle || "";
    const handleMatch = handleId.match(/-(\d+)$/);
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const handleIndex = handleMatch ? parseInt(handleMatch[1], 10) : 0;

    const maxDownstream = sourceStep.maxDownstream();
    if (handleIndex >= maxDownstream) {
      return;
    }

    const updatedStep = updateStepConnection(
      sourceStep,
      handleIndex,
      connection.target,
    );

    setStepWithId(connection.source, () => updatedStep);
    setModified(true);
  });

  const onEdgesDelete = useHandler((edgesToDelete: Edge[]) => {
    if (edgesToDelete.length === 0) return;

    setUndoRedoState((prev) => {
      const updatedSteps = new Map(prev.steps);

      for (const edge of edgesToDelete) {
        if (!edge.source || !edge.target) continue;

        const sourceStep = updatedSteps.get(edge.source);
        if (!sourceStep) {
          continue;
        }

        const updatedStep = removeStepConnection(sourceStep, edge.target);
        updatedSteps.set(edge.source, updatedStep);
      }

      return { ...prev, steps: updatedSteps };
    });
    setModified(true);
  });

  const detectCycles = useHandler((): boolean => {
    const nodes = reactFlowInstance.getNodes();
    const edges = reactFlowInstance.getEdges();

    const visited = new Set<string>();
    const recursionStack = new Set<string>();

    const hasCycle = (nodeId: string): boolean => {
      visited.add(nodeId);
      recursionStack.add(nodeId);

      const outgoingEdges = edges.filter((edge) => edge.source === nodeId);

      for (const edge of outgoingEdges) {
        const targetId = edge.target;
        if (!targetId) continue;

        if (!visited.has(targetId)) {
          if (hasCycle(targetId)) {
            return true;
          }
        } else if (recursionStack.has(targetId)) {
          return true;
        }
      }

      recursionStack.delete(nodeId);
      return false;
    };

    for (const node of nodes) {
      if (!visited.has(node.id) && hasCycle(node.id)) {
        return true;
      }
    }

    return false;
  });

  function getSaveDisabledMessage(): string | undefined {
    if (saveLoad.pending) {
      return "Saving your changes...";
    }
    if (flowType === FlowType.RULE && !ruleName) {
      return "You must name the rule before saving";
    }
    const hasInvalidStep = [...steps.values()].some(
      (step) => !step.valid(flowType),
    );
    if (hasInvalidStep) {
      return "One or more of your steps are invalid. Update the steps then try again.";
    }
    if (detectCycles()) {
      return "Your flow contains a potential infinite loop. Remove this loop before saving.";
    }
    return undefined;
  }

  const reset = useHandler(() => {
    const { model, hasExistingFlow } = parsedFlow;

    const newSteps = modelToSteps({
      model,
      firstStepTitle,
      returnReasonTitle,
      flowType,
      startIcon,
      ...modelToStepsDetailsProps,
    });
    const nodePositions = reconstructNodePositionsMap(
      model.nodePositions,
      newSteps,
    );

    resetUndoRedo({
      steps: newSteps,
      userNodePositions: nodePositions,
      selectedId: undefined,
    });

    if (hasExistingFlow) {
      setPublished(!!model.publishedAt);
      setModified(false);
    } else {
      setPublished(false);
      setModified(true);
    }
    setRuleName(initialRuleName);
    setRuleDescription(initialRuleDescription);
  });

  const resetNodePositions = useHandler(() => {
    setUndoRedoState((prev) => ({ ...prev, userNodePositions: new Map() }));
    setModified(true);
  });

  const cancelClicked = useHandler(() => {
    reset();
  });

  const saveClicked = useHandler(async () => {
    const flowModel = stepsToModel(steps);

    const currentNodes = reactFlowInstance.getNodes();

    // Build a map from current editor step IDs to their positions
    const positionsByStepId = new Map<string, { x: number; y: number }>();
    for (const stepId of steps.keys()) {
      if (stepId === "start") continue;
      const userPosition = userNodePositions.get(stepId);
      const currentNode = currentNodes.find((n) => n.id === stepId);
      const position = userPosition || currentNode?.position || { x: 0, y: 0 };
      positionsByStepId.set(stepId, position);
    }

    // Get step IDs in the order they'll appear in the model (after stepsToModel assigns new IDs)
    const orderedEditorStepIds = getStepIdsInModelOrder(steps);

    // Now build nodePositions array matching the model steps order
    const nodePositions = orderedEditorStepIds.map((editorStepId) => {
      return positionsByStepId.get(editorStepId) || { x: 0, y: 0 };
    });

    const flowWithPositions = { ...flowModel, nodePositions };

    await handleSave(
      returnFlowJsonFormat.write(flowWithPositions),
      undefined,
      ruleName,
      ruleDescription,
    );
    setModified(false);
    setSelectedId(undefined);
  });

  const publishClicked = () => {
    if (handlePublish) {
      sinkPromise(handlePublish());
    }
  };

  const unpublishClicked = () => {
    if (handleUnpublish) {
      sinkPromise(handleUnpublish());
    }
  };

  const detailsClicked = () => {};

  const toggleSelectMode = useHandler(() => {
    setIsSelectMode(true);
  });

  const toggleGrabMode = useHandler(() => {
    setIsSelectMode(false);
  });

  const toggleFullscreen = useHandler(() => {
    if (reactFlowInstance) {
      sinkPromise(reactFlowInstance.fitView({ padding: 0.1, duration: 300 }));
    }
  });

  const handleUndoClick = useHandler(() => {
    undo();
  });

  const handleRedoClick = useHandler(() => {
    redo();
  });

  const flowEditorHeaderButtons = (
    <FlowEditorHeaderButtons
      cancelClicked={cancelClicked}
      cancelDisabled={saveLoad.pending}
      closeNavigationPath={closeNavigationPath}
      defaultButtonClicked={defaultButtonClicked}
      defaultButtonPending={defaultButtonPending}
      defaultButtonText={defaultButtonText}
      detailsClicked={detailsClicked}
      flowType={flowType}
      isNewEditor
      publishClicked={publishClicked}
      publishPending={publishLoad?.pending ?? false}
      rightPanelOpen={!!selectedId}
      ruleName={ruleName}
      saveClicked={saveClicked}
      saveDisabled={getSaveDisabledMessage()}
      savePending={saveLoad.pending}
      setRuleName={setRuleName}
      showPublishButton={!modified && !!handlePublish && published === false}
      showSaveAndCancel={modified}
      showUnpublishButton={!modified && !!handleUnpublish && published === true}
      unpublishClicked={unpublishClicked}
    />
  );

  const flowModel = useMemo(() => {
    return returnFlowJsonFormat.read(questionFlow);
  }, [questionFlow]);

  if (steps.size === 0) {
    return (
      <Flex
        align="center"
        className={styles.emptyStateContainer}
        justify="center"
      >
        <Text className={styles.emptyStateText}>
          No flow steps found. Check the flow configuration.
        </Text>
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="none" height="full" w="full">
      <FlowEditorTopHeader
        buttons={flowEditorHeaderButtons}
        flowType={flowType}
        onReverted={onReverted}
        publishedAt={flowModel?.publishedAt}
        title={flowTitle}
      />

      <Flex
        className={styles.flowEditorContainer}
        dir="row"
        gap="none"
        style={{ position: "relative" }}
      >
        <FlowLeftPanel
          actionsSectionName={actionsSectionName}
          flowType={flowType}
          stepOptions={stepOptions}
          warningSteps={warningSteps}
        />

        <Flex
          className={styles.mainCanvasContainer}
          flex={1}
          style={{ position: "relative" }}
        >
          <FlowCanvas
            className={`${styles.flowEditor} ${isSelectMode ? styles.selectMode : ""}`}
            edges={edges}
            fitView
            fitViewOptions={{ padding: 0.15, maxZoom: 1.2 }}
            mode="edit"
            nodes={nodes}
            nodesDraggable={isSelectMode}
            onConnect={onConnect}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onEdgesChange={onEdgesChange}
            onEdgesDelete={onEdgesDelete}
            onNodeClick={onNodeClick}
            onNodeMouseEnter={onNodeMouseEnter}
            onNodeMouseLeave={onNodeMouseLeave}
            onNodesChange={handleNodesChange}
            onPaneClick={onPaneClick}
            panOnDrag={isSelectMode ? [1] : [0, 1]}
            selectionOnDrag={isSelectMode}
            snapGrid={[GRID_SIZE, GRID_SIZE]}
            snapToGrid
          >
            <ConnectionModeOverlay
              connectionMode={connectionMode}
              hoveredNodeId={hoveredNodeId}
              mousePosition={mousePosition}
              nodes={nodes}
            />
            <FlowEditorToolbar
              canRedo={canRedo}
              canUndo={canUndo}
              handleRedoClick={handleRedoClick}
              handleUndoClick={handleUndoClick}
              isMac={isMac}
              isSelectMode={isSelectMode}
              resetNodePositions={resetNodePositions}
              toggleFullscreen={toggleFullscreen}
              toggleGrabMode={toggleGrabMode}
              toggleSelectMode={toggleSelectMode}
            />
          </FlowCanvas>

          {/* Commented out CREATE mode - using Q&A mode instead */}
          {/* <FlowAgentChat
            flowType={flowType}
            onFlowChange={(flow) => {
              setUndoRedoState((prev) => ({
                ...prev,
                steps: modelToSteps({
                  model: flow,
                  firstStepTitle,
                  returnReasonTitle,
                  flowType,
                  ...modelToStepsDetailsProps,
                }),
              }));
              setModified(true);
            }}
          /> */}

          <FlowAgentChat
            flow={flowModel}
            flowType={flowType}
            onFlowChange={(flow) => {
              setUndoRedoState((prev) => ({
                ...prev,
                steps: modelToSteps({
                  model: flow,
                  firstStepTitle,
                  returnReasonTitle,
                  flowType,
                  ...modelToStepsDetailsProps,
                }),
              }));
              setModified(true);
            }}
          />
        </Flex>

        <RightPanel
          connectionMode={connectionMode}
          flowType={flowType}
          onClose={() => setSelectedId(undefined)}
          onDelete={handleDeleteStep}
          onStepChange={setStepWithId}
          show={!!selectedId}
          startConnectionMode={startConnectionMode}
          step={selectedStep}
          stepId={selectedId}
          stepOptions={stepOptions}
          steps={steps}
        />
      </Flex>
    </Flex>
  );
});

const NewFlowEditorReadonlyInner = memo(function NewFlowEditorReadonlyInner({
  steps,
  flowType,
  nodePositionsArray,
  team,
  highlightedNodeIds,
  currentStepId,
  highlightedEdgePairs,
  onNodeClick,
}: FlowEditorReadonlyModeProps) {
  const savedNodePositions = useMemo(
    () => reconstructNodePositionsMap(nodePositionsArray, steps),
    [nodePositionsArray, steps],
  );

  const emptySelectedRef = useRef(new Set<string>());

  const highlight = useMemo(
    () =>
      highlightedNodeIds
        ? { highlightedNodeIds, currentStepId, highlightedEdgePairs }
        : undefined,
    [highlightedNodeIds, currentStepId, highlightedEdgePairs],
  );

  const { nodes, edges } = useFlowNodesAndEdges({
    steps,
    flowType,
    nodePositions: savedNodePositions,
    selectedNodeIdsRef: emptySelectedRef,
    team,
    highlight,
  });

  const onInit = useHandler(
    (instance: ReactFlowInstance<Node<NodeData>, Edge>) => {
      const targetNode = currentStepId
        ? nodes.find((n) => n.id === currentStepId)
        : undefined;
      if (targetNode) {
        void instance.setCenter(
          targetNode.position.x + 112,
          targetNode.position.y + 50,
          { zoom: 0.85, duration: 0 },
        );
      } else {
        void instance.fitView({ padding: 0.1, maxZoom: 1 });
      }
    },
  );

  return (
    <FlowCanvas
      edges={edges}
      mode="readonly"
      nodes={nodes}
      onInit={onInit}
      onNodeClick={onNodeClick}
    >
      <Controls showInteractive={false} />
    </FlowCanvas>
  );
});

export const NewFlowEditor = memo(function NewFlowEditor(
  props: NewFlowEditorProps,
) {
  return (
    <ReactFlowProvider>
      {props.mode === "readonly" ? (
        <NewFlowEditorReadonlyInner {...props} />
      ) : (
        <NewFlowEditorInner {...props} />
      )}
    </ReactFlowProvider>
  );
});
